import React, { useContext, useEffect, useRef, useState } from 'react'
import { Autocomplete, Button, FormControl, TextField, Popper, Box } from '@mui/material'
import axios from 'axios'
import { API_ENDPOINT } from '../../../configurations/global.config'
import { useSnackBar } from '../../../contexts/snackbar'
import { AuthContext } from '../../../contexts/user-context/user-context'
import { IArea } from '../../../interfaces/common/location'
import ArabicKeyboard from '../arabic-keyboard'
import { FaEye } from 'react-icons/fa'
import { isDesktop } from 'react-device-detect'

interface AreaInputProps {
  area: IArea | null
  setArea: (area: IArea | null) => void
  disabled?: boolean
}

function AreaInput({ area, setArea, disabled }: AreaInputProps) {
  const [areas, setAreas] = useState<IArea[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showArabicKeyboard, setShowArabicKeyboard] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const { showMessage } = useSnackBar()
  const { config } = useContext(AuthContext)

  const autocompleteRef = useRef<HTMLDivElement>(null)
  const keyboardRef = useRef<HTMLDivElement>(null)

  // Custom Popper component for upward placement
  const CustomPopper = (props: any) => {
    return (
      <Popper
        {...props}
        placement="top"
        modifiers={[
          {
            name: 'flip',
            enabled: false,
          },
        ]}
        style={{ 
          position: 'fixed',
          bottom: '100%',
          top: 'auto !important',
        }}
      />
    )
  }

  // Fetch areas once
  useEffect(() => {
    const fetchAreas = async () => {
      setIsLoading(true)
      try {
        const response = await axios.get<IArea[]>(`${API_ENDPOINT}/v1/areas/`, config)
        const data = response.data
        if (area != null && area.code.startsWith('EXT')) data.push(area)
        setAreas(data.sort((a, b) => (a.district < b.district ? 1 : -1)))
      } catch {
        showMessage('Failed to load areas. Please contact support', 'error')
      } finally {
        setIsLoading(false)
      }
    }
    fetchAreas()
  }, [])

  const selectedValue = areas.find((opt) => opt.id === area?.id) || null

  return (
    <FormControl fullWidth ref={autocompleteRef}>

      <Box display="flex" flexDirection={"row"} gap={0.5}>

          <Autocomplete<IArea, false, false, false>
            fullWidth
            id="area"
            disabled={disabled || isLoading}
            loading={isLoading}
            value={selectedValue}
            onChange={(_, newValue) => {
              setArea(newValue)
              setShowArabicKeyboard(false)
              setInputValue('')
            }}
            options={areas}
            getOptionLabel={(option) => `${option.code} ${option.name_en} - ${option.name_ar}`}
            isOptionEqualToValue={(option, value) => option.name_en === value.name_en}
            groupBy={(option) => option.district}
            inputValue={inputValue}
            onInputChange={(_, newInputValue, reason) => {
              if (!showArabicKeyboard) {
                setInputValue(newInputValue)
              }
            }}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            renderInput={(params) => <TextField {...params} autoComplete="off" label="City / Area" />}
            PopperComponent={showArabicKeyboard ? CustomPopper : undefined}
          />

          {isDesktop && <Button 
            variant='outlined' 
            size="small"
            onClick={() => setShowArabicKeyboard((prev) => !prev)} 
            style={{margin: "auto"}} 
            sx={{mb:2}}
          >
            {showArabicKeyboard ? 'Hide Keyboard' : 'Arabic Keyboard'}
          </Button>}


      </Box>
      
      {showArabicKeyboard && (
        <div ref={keyboardRef} style={{marginTop: 1}}>
          <ArabicKeyboard
            value={inputValue}
            setValue={(v) => {
              setInputValue(v)
              setOpen(true) // Keep dropdown open when typing
            }}
          />
        </div>
      )}
    </FormControl>
  )
}

export default AreaInput